import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { useClient } from '../../../../utils/useClient'
import {
  getSeriesFrame,
  getSessionInitDataState,
  setSeriesFrame,
} from '../../../../store/sessionDataSlice'
import { getMenuState, setMenuTreeAllView } from '../../../../store/menuSlice'
import { useEffect, useRef, useState } from 'react'
import { CustomScrollbarMixin } from '../../../../css/CustomScrollbarMixin'
import { useResizeObserver } from '../../../../utils/useResizeObserver'
import { Size2d } from '../../../../types/Size2d'
import {
  SvgChevronLeft,
  SvgChevronRight,
} from '../../../../components/SvgImports'
import {
  pushAdobeEvent,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingInlineMenuArrowClick,
  ScaniaAdobeTrackingMenuClickEvent,
} from '../../../../utils/adobeAnalytics'
import { MenuContentColourAllView } from './MenuContentColourAllView'
import { MenuContentNonColourAllView } from './MenuContentNonColourAllView'
import { useDrag } from '@use-gesture/react'
import { TestElementTypeId } from '../../../../types/TestAttributeId'

const MenuSectionRoot = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const MenuContentRoot = styled.div`
  ${CustomScrollbarMixin}
  display: flex;
  flex-direction: column;
`

const InlineMenuWrapper = styled.div`
  display: flex;
  touch-action: pan-y;
`

const TabsWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 48px;
  align-items: center;
  border-bottom: 1px solid var(--tds-grey-300);
  overflow-x: hidden;
`

interface TabsButtonProps {
  $isSelected: boolean
}

const TabsButton = styled.div<TabsButtonProps>`
  padding-left: 16px;
  padding-right: 16px;
  width: fit-content;
  height: 100%;
  white-space: nowrap;
  color: ${(props) =>
    props.$isSelected
      ? 'var(--tds-inline-tabs-tab-color-selected)'
      : 'var(--tds-inline-tabs-tab-color)'};
  font-weight: bold;
  border-bottom: ${(props) =>
    props.$isSelected
      ? '2px solid var(--tds-blue-400)'
      : '2px solid transparent'};
  display: flex;
  align-items: center;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      border-bottom: ${(props) =>
        props.$isSelected
          ? '2px solid var(--tds-blue-400)'
          : '2px solid var(--tds-grey-400)'};
    }
  }
`

interface TabsScrollButtonProps {
  $disabled: boolean
}

const TabsScrollButton = styled.div<TabsScrollButtonProps>`
  min-width: 32px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--tds-grey-300);

  svg {
    height: 20px;
    color: var(--tds-blue-900);
    color: ${(props) =>
      props.$disabled ? 'var(--tds-grey-500)' : 'var(--tds-blue-900)'};
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: ${(props) => (props.$disabled ? 'auto' : 'pointer')};
      background-color: var(--tds-grey-300);
    }
  }
`

interface MenuSectionProps {
  handleItemSelection: (id: string, text: string) => void
  highlightedId: string | null
  parentSize: Size2d
}

export function ConfiguratorMenuMobile({
  handleItemSelection,
  highlightedId,
  parentSize,
}: MenuSectionProps): JSX.Element {
  const dispatch = useAppDispatch()
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const client = useClient()
  const menuTree = useAppSelector(getMenuState).menuResultAllView?.menuTree
  const [selectedMenuLevel2, setSelectedMenuLevel2] = useState<
    string | null | undefined
  >(null)
  const [selectedMenuLevel3, setSelectedMenuLevel3] = useState<
    string | null | undefined
  >(null)
  const [isColorMenu, setIsColorMenu] = useState<boolean>(false)
  const tabsWrapperRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [inlineMenuisOverflowing, setInlineMenuIsOverflowing] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const size = useResizeObserver<Size2d>(rootRef, { width: 0, height: 0 })
  const seriesFrame = useAppSelector(getSeriesFrame)
  const interiorMenuIdAllView =
    useAppSelector(getMenuState).menuResultAllView?.interiorMenuIdAllView

  useEffect(() => {
    const menuLevel2 = menuTree?.subMenus
      ? menuTree?.subMenus.find((menu) => menu.isOpen === true)?.subMenus
      : null
    const menuLevel3 = menuLevel2
      ? menuLevel2?.find((menu) => menu.isOpen === true)?.subMenus
      : null
    const selectedMenuLevel2 = menuLevel2?.find(
      (item) => item.isOpen === true,
    )?.id
    const selectedMenuLevel3node = menuLevel3?.find(
      (item) => item.isOpen === true,
    )
    const selectedMenuLevel3 = selectedMenuLevel3node?.id
    const isColorMenu = selectedMenuLevel3node?.leafItems
      ? selectedMenuLevel3node?.leafItems[0].isColorMenu
      : false
    setSelectedMenuLevel2(selectedMenuLevel2)
    setSelectedMenuLevel3(selectedMenuLevel3)
    setIsColorMenu(isColorMenu)
  }, [menuTree])

  const handleInlineTabClick = (menuId: string, text: string) => {
    if (!sessionInitData?.sessionId) {
      return
    }
    if (!client) {
      return
    }
    const asyncWrapper = async () => {
      const newMenuState = await client.exploreMenu({
        sessionId: sessionInitData.sessionId,
        menuId: menuId,
        showAllConfigMenu: true,
      })
      if (newMenuState.success) {
        dispatch(setMenuTreeAllView(newMenuState.success))

        if (
          menuTree?.subMenus &&
          interiorMenuIdAllView &&
          menuTree.subMenus?.find((menu) => menu.id === interiorMenuIdAllView)
            ?.isOpen
        ) {
          if (seriesFrame.seriesId !== 'INTERIOR') {
            dispatch(setSeriesFrame({ seriesId: 'INTERIOR', frameId: 1 }))
          }
        } else if (seriesFrame.seriesId === 'INTERIOR') {
          dispatch(setSeriesFrame({ seriesId: 'EXTERIOR', frameId: 2 }))
        }

        const trackingEvent: ScaniaAdobeTrackingMenuClickEvent = {
          event: ScaniaAdobeEventId.MenuClick,
          eventInfo: {
            optionClickedId: menuId,
            optionClickedText: text,
          },
        }
        pushAdobeEvent(trackingEvent)
      }
    }
    asyncWrapper()
  }

  // Scrolls the inlineTabs menu left or right
  const handleScrollTabsClick = (direction: 'left' | 'right') => {
    if (tabsWrapperRef.current) {
      const scrollAmount = 100
      const newScrollLeft =
        direction === 'left'
          ? tabsWrapperRef.current.scrollLeft - scrollAmount
          : tabsWrapperRef.current.scrollLeft + scrollAmount
      tabsWrapperRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'instant',
      })
      updateScrollState()

      const trackingEvent: ScaniaAdobeTrackingInlineMenuArrowClick = {
        event: ScaniaAdobeEventId.InlineMenuArrowClick,
        eventInfo: {
          direction: direction,
        },
      }
      pushAdobeEvent(trackingEvent)
    }
  }

  // Update scroll state of inline tabs
  useEffect(() => {
    updateScrollState()
  }, [size, tabsWrapperRef, menuTree])

  const updateScrollState = () => {
    if (tabsWrapperRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsWrapperRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(Math.round(scrollLeft) < scrollWidth - clientWidth - 1)
      setInlineMenuIsOverflowing(scrollWidth > clientWidth)
    }
  }

  const bindDrag = useDrag(
    (drag) => {
      const swipeThreshold = 0.1
      if (tabsWrapperRef.current && drag.velocity[0] > swipeThreshold) {
        const newScrollLeft =
          tabsWrapperRef.current.scrollLeft - drag.movement[0]
        tabsWrapperRef.current.scrollTo({
          left: newScrollLeft,
          behavior: 'instant',
        })
        updateScrollState()
      }
    },
    {
      axis: 'x',
    },
  )

  return (
    <MenuSectionRoot>
      <InlineMenuWrapper {...bindDrag()}>
        {inlineMenuisOverflowing && (
          <TabsScrollButton
            onClick={() => handleScrollTabsClick('left')}
            $disabled={!canScrollLeft}
            key={'left' + canScrollLeft.toString()}
            data-test-element-type={
              TestElementTypeId.GuidedViewTabsScrollLeftButton
            }
          >
            <SvgChevronLeft />
          </TabsScrollButton>
        )}
        <TabsWrapper ref={tabsWrapperRef}>
          {menuTree?.subMenus &&
            menuTree.subMenus.map((menuItem, i) => {
              return (
                <TabsButton
                  onClick={() =>
                    handleInlineTabClick(menuItem.id, menuItem.shortText || '')
                  }
                  key={menuItem.id + 'guidedView'}
                  $isSelected={menuItem.isOpen}
                  data-test-element-type={TestElementTypeId.AllViewTabsButton}
                  data-test-etel-variant-id={menuItem.id}
                >
                  {i + 1 + '.  ' + (menuItem.shortText || menuItem.id)}
                </TabsButton>
              )
            })}
        </TabsWrapper>
        {inlineMenuisOverflowing && (
          <TabsScrollButton
            onClick={() => handleScrollTabsClick('right')}
            $disabled={!canScrollRight}
            key={'right' + canScrollRight.toString()}
            data-test-element-type={
              TestElementTypeId.GuidedViewTabsScrollRightButton
            }
          >
            <SvgChevronRight />
          </TabsScrollButton>
        )}
      </InlineMenuWrapper>

      <MenuContentRoot key={'menuroot' + selectedMenuLevel3}>
        {isColorMenu ? (
          <MenuContentColourAllView
            parentSize={parentSize}
            handleItemSelection={handleItemSelection}
            highlightedId={highlightedId}
            selectedMenuLevel2={selectedMenuLevel2}
            selectedMenuLevel3={selectedMenuLevel3}
          />
        ) : (
          <MenuContentNonColourAllView
            parentSize={parentSize}
            handleItemSelection={handleItemSelection}
            highlightedId={highlightedId}
            selectedMenuLevel2={selectedMenuLevel2}
            selectedMenuLevel3={selectedMenuLevel3}
          />
        )}
      </MenuContentRoot>
    </MenuSectionRoot>
  )
}
